import * as React from 'react'
import {
  Button,
  Form,
  Snackbar,
  Loader,
  Paper,
  Grid,
  Typography,
  Autocomplete,
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import { FormikTextField, CenteredContainer } from 'components'
import {
  makeStyles,
  createStyles,
  Theme,
  useTheme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import * as Yup from 'yup'
import { useLogger, useFormFactor, useProfileApi, useAuth } from 'hooks'
import { ApiUpdateEmailAddressViewModel } from '../api'
import clsx from 'clsx'

interface VerifyEmailAddressProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      boxShadow: '0px 1px 2px rgba(0, 0, 0,0.06)',
      backgroundColor: theme.palette.background.paper,
    },
    root: {
      flexGrow: 1,
      minHeight: 0,
    },
    centeredContent: {
      [theme.breakpoints.down('sm')]: {
        margin: 0,
      },
    },
    form: {
      padding: theme.spacing(8),
      width: 300,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    formItem: {
      // we need 35px between form items, but the helper text takes up 12
      marginTop: 23,
      width: '100%',
    },
    textItem: {
      width: '100%',
    },
    loaderContainer: {
      width: '100%',
    },
    firstFormItem: {
      width: '100%',
    },
    buttonBar: {
      width: '100%',
      marginTop: theme.spacing(8),
    },
    associatedFormItem: {},
    confirmationModalFormItem: {
      // since we don't have a field above this, set it to the full 35px
      marginTop: 35,
      marginBottom: 35,
    },
    actionAndLoader: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    loader: {
      flexGrow: 1,
    },
    removePadding: {
      '& .MuiAutocomplete-inputRoot': {
        paddingRight: '0',
      },
    },
    subHeader: {
      marginTop: theme.spacing(4),
    },
  }),
)

const VerifyEmailAddressSchema = Yup.object().shape({
  emailAddress: Yup.string()
    .email('The Email field must be a valid email address')
    .required('The Email field is required.'),
})

export const VerifyEmailAddress: React.FunctionComponent<VerifyEmailAddressProps> = () => {
  const classes = useStyles({})
  const { logError } = useLogger()
  const [error, setError] = React.useState<string | null>(null)
  const formFactor = useFormFactor()
  const { dispatch: dispatchAuthAction } = useAuth()
  const theme = useTheme<Theme>()
  const [emailOptions, setEmailOptions] = React.useState<string[]>([])

  const { updateOwnEmailAddress, getOwnEmailAddressOptions } = useProfileApi()

  const initialValues: ApiUpdateEmailAddressViewModel = {
    emailAddress: '',
  }

  React.useEffect(() => {
    const loadAgencyEmailAddressesForSelf = async () => {
      const response = await getOwnEmailAddressOptions()
      if (response.status === 200 && response.data.isSuccess) {
        setEmailOptions(response.data.data.emailAddresses)
      }
    }

    loadAgencyEmailAddressesForSelf()
  }, [])

  const submitEmailVerification = async (
    values: ApiUpdateEmailAddressViewModel,
  ) => {
    try {
      const response = await updateOwnEmailAddress(values)
      if (response.status === 200) {
        dispatchAuthAction({
          type: 'UpdateEmailAddress',
          payload: values.emailAddress,
        })
      } else {
        setError('There was an error updating your email address')
      }
    } catch (e) {
      logError(error)

      setError('There was an error updating your email address')
    }
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={VerifyEmailAddressSchema}
        onSubmit={submitEmailVerification}
      >
        {(formikProps) => (
          <CenteredContainer
            className={classes.root}
            disableVerticalGutters={
              formFactor.isPhone && !formikProps.isSubmitting
            }
            contentClassName={classes.centeredContent}
          >
            <Paper className={classes.paper}>
              <Form
                onSubmit={formikProps.handleSubmit}
                className={classes.form}
              >
                <Grid
                  container
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  {!formikProps.isSubmitting && (
                    <React.Fragment>
                      <Grid item className={classes.textItem}>
                        <Typography variant="body1">
                          Please enter your email address.
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.subHeader}
                        >
                          We use this email to send important service related
                          notices regarding outages and other critical messages.
                        </Typography>
                      </Grid>
                      <Grid item className={classes.formItem}>
                        <Autocomplete
                          id="emailAddress"
                          className={classes.removePadding}
                          freeSolo={true}
                          disableClearable={true}
                          options={emailOptions}
                          defaultValue={
                            formikProps.values.emailAddress ??
                            emailOptions[0] ??
                            ''
                          }
                          onChange={(event, newValue) => {
                            formikProps.setFieldValue('emailAddress', newValue)
                          }}
                          onBlur={formikProps.handleBlur}
                          renderInput={(params) => (
                            <FormikTextField
                              {...params}
                              InputLabelProps={{
                                ...params.InputLabelProps,
                                ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                              }}
                              label="Email"
                              formikProps={formikProps}
                              fieldName="emailAddress"
                              type="email"
                              fullWidth
                              required
                            />
                          )}
                        />
                      </Grid>
                    </React.Fragment>
                  )}
                  {formikProps.isSubmitting && (
                    <Grid item className={classes.loaderContainer}>
                      <Loader
                        size="sm"
                        text="Signing In"
                        disableVerticalGutters
                        className={classes.loader}
                      />
                    </Grid>
                  )}
                  {!formikProps.isSubmitting && (
                    <Grid
                      item
                      className={clsx(
                        classes.buttonBar,
                        classes.actionAndLoader,
                      )}
                    >
                      <Button type="submit" color="primary">
                        Update Email Address
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Form>
            </Paper>
          </CenteredContainer>
        )}
      </Formik>
      <Snackbar
        severity="error"
        open={error !== null}
        onClose={() => setError(null)}
      >
        {error}
      </Snackbar>
    </React.Fragment>
  );
}
